import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import {
  IPasswordOptions,
  IPasswordValidatorResult,
  IPasswordValidationStatus,
  IPasswordValues
} from '../../../utils/constants/interfaces';
import styles from './PasswordValidator.module.css';
import { Helpers } from '../../../utils/helpers/Helpers';
import Container from '../Container/Container';
import FragmentContainer from '../FragmentContainer/FragmentContainer';
import { ValidationListItem } from '../ValidationListItem/ValidationListItem';

interface IPasswordValidatorProps {
  isValidCallback: (returnValue: IPasswordValidatorResult) => void;
  passwordOptions?: IPasswordOptions;
}

export const PasswordValidator = ({
  isValidCallback,
  passwordOptions = {
    requireCurrentPassword: false,
    requireLowercase: true,
    requireUppercase: true,
    requireNumber: true,
    requireSymbol: true,
    minimumLength: 8
  }
}: IPasswordValidatorProps) => {
  const [validationStatus, setValidationStatus] = useState<IPasswordValidationStatus>({
    isValid: false,
    hasLowercase: false,
    hasUppercase: false,
    hasNumbers: false,
    hasSymbol: false,
    isLongEnough: false,
    passwordsMatch: false
  });

  const [passwords, setPasswords] = useState<IPasswordValues>({
    newPassword: '',
    confirmNewPassword: '',
    currentPassword: ''
  });

  const updatePasswordValue = (key: string, value: string) => {
    const pass = { ...passwords };
    pass[key] = value;
    setPasswords(pass);
    const valStatus = Helpers.validatePassword(
      pass.newPassword,
      pass.confirmNewPassword,
      passwordOptions
    );
    setValidationStatus(valStatus);
    isValidCallback({
      isValid: valStatus.isValid,
      currentPassword: pass.currentPassword,
      newPassword: pass.newPassword
    });
  };

  return (
    <>
      <Container
        visible={
          (!validationStatus.isValid || !validationStatus.passwordsMatch) &&
          passwords.newPassword.length > 0
        }>
        <div className={styles.validationResults}>
          Your password must meet the following requirements:
          <ul>
            <ValidationListItem
              label="Must contain at least one uppercase letter"
              visible={passwordOptions.requireUppercase}
              isValid={validationStatus.hasUppercase}
            />
            <ValidationListItem
              label="Must contain at least one lowercase letter"
              visible={passwordOptions.requireLowercase}
              isValid={validationStatus.hasLowercase}
            />
            <ValidationListItem
              label="Must contain at least one number"
              visible={passwordOptions.requireNumber}
              isValid={validationStatus.hasNumbers}
            />
            <ValidationListItem
              label="Must contain at least one special character"
              visible={passwordOptions.requireSymbol}
              isValid={validationStatus.hasSymbol}
            />
            <ValidationListItem
              label={`Must be at least ${passwordOptions.minimumLength} characters long`}
              visible={true}
              isValid={validationStatus.isLongEnough}
            />
            <ValidationListItem
              label="Both password fields must match"
              visible={true}
              isValid={validationStatus.passwordsMatch}
            />
          </ul>
        </div>
      </Container>
      <div>
        <Container visible={passwordOptions.requireCurrentPassword}>
          <label htmlFor="currentPassword">Current Password</label>
          <input
            type="password"
            id="currentPassword"
            className="form-control"
            value={passwords.currentPassword}
            autoFocus={passwordOptions.requireCurrentPassword}
            onChange={(e: React.FormEvent<HTMLInputElement>) =>
              updatePasswordValue('currentPassword', e.currentTarget.value)
            }
          />
        </Container>

        <label htmlFor="newPassword">New Password</label>
        <FragmentContainer visible={validationStatus.isValid}>
          <FontAwesomeIcon className="mx-1 status-icon text-success" icon={faCircleCheck} />
        </FragmentContainer>
        <input
          type="password"
          id="newPassword"
          className="form-control"
          value={passwords.newPassword}
          autoFocus={!passwordOptions.requireCurrentPassword}
          autoComplete="new-password"
          onChange={(e: React.FormEvent<HTMLInputElement>) =>
            updatePasswordValue('newPassword', e.currentTarget.value)
          }
        />

        <label htmlFor="confirmNewPassword">Confirm New Password</label>
        {validationStatus.passwordsMatch && (
          <FontAwesomeIcon className="mx-1 status-icon text-success" icon={faCircleCheck} />
        )}
        <input
          type="password"
          id="confirmNewPassword"
          className="form-control"
          value={passwords.confirmNewPassword}
          autoComplete="new-password"
          onChange={(e: React.FormEvent<HTMLInputElement>) =>
            updatePasswordValue('confirmNewPassword', e.currentTarget.value)
          }
        />
      </div>
    </>
  );
};
